import React from 'react';
import isEmpty from 'lodash/isEmpty';
import parse from 'html-react-parser';
import ReactGA from 'react-ga4';

import { Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import { HotelImagesSlider } from '../hotel-image-slider';
import { IImage, IAccount, ITrustYouReview } from '@share/common-types';

import { FullScreenSvg, ImagesQuantitySvg, CheckSvg } from '@assets';
import { D_CLICK_IMAGES } from '@share/constants';
import { NOT_IMAGE_PROPERTY } from '@constants';

import NotImageImg from '@assets/images/not_image_room.png';

import './style.scss';
import { HotelInfo } from '../hotel-info';
import { IHotelDetails } from '@common-types';

interface IProps {
  account: IAccount | null | undefined;
  images: IImage[];
  hotelName: string;
  hotelDescription: string[];
  additionalDescriptions: string[];
  hotelRooms: number;
  isStaticDetail: boolean | undefined;
  
  showMap: () => void;
  hotel: IHotelDetails;
  reviewModule: ITrustYouReview;
  roomsRef: React.RefObject<HTMLDivElement>;
  overviewRef: React.RefObject<HTMLDivElement>;
  onClickScrollToReviews: (e: React.MouseEvent<HTMLDivElement>) => void;
  filtersAnchor: () => React.ReactNode;
  onClickScrollToRooms?: (e: React.MouseEvent<HTMLDivElement>) => void;

  thumb?: string;
}

interface IState {
  isModalVisible: boolean;
  startIndex: number;
  isEllipsis: boolean;
  showMoreLink: boolean;
}

const FIRST_ELEMENT_ARRAY = 0;
const MIN_IMAGE = 0;
const MAX_IMAGE = 4;
const ONE_SECOND = 1000;

const { Paragraph } = Typography;

export class HotelImagesDescription extends React.Component<IProps, IState> {
  state: IState = {
    isModalVisible: false,
    startIndex: 0,
    isEllipsis: true,
    showMoreLink: false,
  };

  showSlider = (index: number): void => {
    this.setState({ isModalVisible: true });
    this.setState({ startIndex: index });

    const { account } = this.props;
    ReactGA.event({
      category: account?.name || '',
      action: `${D_CLICK_IMAGES}_${account?.name.toUpperCase()}`,
      label: `User clicked an image on detail`,
      nonInteraction: false,
    });
  };

  handleCancel = (e: React.MouseEvent<HTMLDivElement>, isScrollToRooms = false): void => {
    if (isScrollToRooms) {
      this.props.onClickScrollToRooms?.(e);
    }

    this.setState({ isModalVisible: false });
  };

  showMore = (): void => {
    this.setState({ isEllipsis: !this.state.isEllipsis });
  };

  hasNotRealImageProperty = (): boolean => {
    // First check the thumb property
    if (this.props.thumb && !isEmpty(this.props.thumb) && this.props.thumb.toLowerCase().includes(NOT_IMAGE_PROPERTY)) {
      return true;
    }
    
    // If thumb check fails, validate the images array
    return !!(this.props.images?.some((item) => 
      item?.url && item?.url.toLowerCase().includes(NOT_IMAGE_PROPERTY)
    ));
  };

  componentDidMount(): void {
    setTimeout(() => {
      const moreLink: HTMLLinkElement | null = document.querySelector(
        '.hotel-images__hotel-text .ant-typography-expand',
      );

      if (moreLink) {
        this.setState({ showMoreLink: true });
      }
    }, ONE_SECOND);
  }

  getOtherImages = (images: IImage[]): React.ReactNode =>
    images.map((img: IImage, i: number) => {
      if (i === MIN_IMAGE || i > MAX_IMAGE || !img || !img.url) {
        return;
      }
  
      return (
        <div key={i} className="hotel-images__other-image-wrapper">
          <img className="hotel-images__image" src={img.url} alt={img.description} />
          <div className="hotel-images__middle">
            <div
              className="hotel-images__btn-hover-wrapper"
              onClick={() => this.showSlider(Number(i))}
            >
              <FullScreenSvg />
              <span className="hotel-images__btn-hover-text">
                <FormattedMessage id="full.view" />
              </span>
            </div>
          </div>
        </div>
      );  
    });

  renderHotelInfo = () => {
    const {
      account,
      showMap,
      hotel,
      onClickScrollToRooms,
      reviewModule,
      roomsRef,
      onClickScrollToReviews,
    } = this.props;

    return (
      <div className="hotel-details-wrapper__desktop-hotel-info">
        <HotelInfo
          showBigMap={showMap}
          hotel={hotel as IHotelDetails}
          onClickScrollToRooms={onClickScrollToRooms}
          reviewModule={reviewModule}
          roomsRef={roomsRef}
          account={account}
          onClickScrollToReviews={onClickScrollToReviews}
        />
      </div>
    );
  };
  
  render(): React.ReactNode {
    const {
      images = [],
      hotelName,
      hotelDescription = [],
      hotelRooms,
      account,
      isStaticDetail,

      hotel,
      overviewRef,
    } = this.props;
    const { isModalVisible, startIndex, isEllipsis, showMoreLink } = this.state;

    const firstImage = images?.length ? images[FIRST_ELEMENT_ARRAY] : { url: NotImageImg, width: 450, description: 'not found image' };
    const hasImages = images?.length > 0;
    const firstImageThumb = this.hasNotRealImageProperty() ? this.props.thumb : firstImage?.url ? firstImage.url : NotImageImg;    
    const firstImageDescription = this.hasNotRealImageProperty() ? this.props.thumb : firstImage?.description? firstImage.description : 'not found image';

    const notRealImageProperty = this.hasNotRealImageProperty();
    const styleImg: any = {};
    if (notRealImageProperty) {
      styleImg.filter = 'grayscale(100%)';
    }

    const isAshevillePlay = account?.isAshevillePlay;
    
    const padding = ((document.body.offsetWidth - 1200) / 2) + 30;
    const styleImagesQuantity = isAshevillePlay ?{ right: padding } : {};
    const styleImagesMiddle = isAshevillePlay ?{ left: padding } : {};

    return (
      <>
      <div className={`hotel-images ${isAshevillePlay ? 'asheville-play-images' : ''}`}>
        <div className="hotel-images__wrapper">
          {isAshevillePlay && (
            <div className="hotel-images__wrapper-background"></div>)}

          <div className="hotel-images__main-image-wrapper">
            <img
              className={`hotel-images__image ${hasImages ? '' : 'disabled-hover'}`}
              src={firstImageThumb}
              width={firstImage?.width? firstImage.width : 450}
              alt={firstImageDescription}
              style={styleImg}
            />
            {!hasImages && !this.hasNotRealImageProperty() && (<div className="hotel-images__no-images">No images found</div>)}
            {hasImages && !this.hasNotRealImageProperty() && (<div className="hotel-images__middle" style={styleImagesMiddle}>
              <div
                className="hotel-images__btn-hover-wrapper"
                onClick={() => this.showSlider(FIRST_ELEMENT_ARRAY)}
              >
                <FullScreenSvg />
                <span className="hotel-images__btn-hover-text">
                  <FormattedMessage id="full.view" />
                </span>
              </div>
            </div>)}
            {this.hasNotRealImageProperty() && (
              <div style={{ position: 'absolute', top: '0', right: '0', bottom: '0', left: '0', background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
                <p className="condo-hotel-card__label-title" style={{ color: 'white', fontSize: '12px' }}>
                  <FormattedMessage
                    id="not.real.image.property"
                    defaultMessage="Not actual photo of the property"
                  />
                </p>
              </div>)}

            {!this.hasNotRealImageProperty() && (
              <div className="hotel-images__images-quantity" style={styleImagesQuantity}>
                <ImagesQuantitySvg />
                <span className="hotel-images__images-quantity-text">{images.length}+</span>
              </div>)}
          </div>
          {(hasImages && !isAshevillePlay && !this.hasNotRealImageProperty()) && (<div className="hotel-images__other-images-wrapper">{this.getOtherImages(images)}</div>)}

        </div>

        <div className="hotel-details-wrapper__details-anchor">
          {hotel && this.props.filtersAnchor()}
        </div>

        {(hasImages && !this.hasNotRealImageProperty()) && (
          <HotelImagesSlider
            images={images}
            onCancel={this.handleCancel as any}
            visible={isModalVisible}
            hotelName={hotelName}
            startIndex={startIndex}
          />)}

        <div ref={overviewRef} />

        {isAshevillePlay && this.renderHotelInfo()}

        <div className="hotel-images">
          {!isAshevillePlay && this.renderHotelInfo()}

          <div className="hotel-images__hotel-info">
            <div className="hotel-images__hotel-description">
              {!!hotelDescription?.filter(item => item !== null && item !== undefined).length && (
                <div className="hotel-images__hotel-text">
                  <Paragraph ellipsis={isEllipsis ? { rows: 6, expandable: true } : false}>
                    {hotelDescription.filter(item => item !== null && item !== undefined).map((hd, i) => <div key={i}><span style={{ marginRight: '0.5em' }}><CheckSvg /></span>{parse(hd)}</div>)}
                  </Paragraph>

                  {!!showMoreLink && (
                    <p className="hotel-images__read-more-button" onClick={this.showMore}>
                      {isEllipsis ? (
                        <FormattedMessage id="show.more" />
                      ) : (
                        <FormattedMessage id="show.less" />
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {!!hotelRooms && !isStaticDetail && (
              <div className="hotel-images__hotel-info-rooms">
                <p className="hotel-images__hotel-room-title">
                  <FormattedMessage id="number.of.rooms" />:
                </p>
                <span className="hotel-images__rooms-quantity">{hotelRooms}</span>
              </div>
            )}
          </div>
        </div>
        </div>
      </>
    );
  }
}
